import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { 
  FiBell, FiSettings, FiLogOut, FiUser, FiHelpCircle, 
  FiMenu, FiX, FiSearch, FiMoon, FiSun, FiChevronDown,
  FiMaximize, FiMinimize 
} from 'react-icons/fi';
import axiosConfig from "../../api/axiosConfig";
import NotificationBell from '../NotificationBell';

const AdminLayout = () => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stats, setStats] = useState({
    studentCount: 0,
    teacherCount: 0,
    lessonCount: 0
  });
  
  const profileRef = useRef(null);
  const navigate = useNavigate();

  const adminName = localStorage.getItem("adminName") || "Admin";
  const [token, setTokenState] = useState(localStorage.getItem("token"));

  // Token kontrolü - token yoksa login sayfasına yönlendir
  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem("token");
      if (!currentToken) {
        navigate('/admin/login');
      } else {
        setTokenState(currentToken);
      }
    };
    
    checkToken();
    
    // Storage değişikliklerini dinle (başka sekmelerden çıkış vs.)
    window.addEventListener('storage', checkToken);
    return () => window.removeEventListener('storage', checkToken);
  }, [navigate]);

  // Dark mode değişikliğini kaydet
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Fullscreen toggle fonksiyonu
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Tam ekran hatası:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Tam ekrandan çıkış hatası:', err);
      });
    }
  };

  // Fullscreen değişikliklerini dinle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // ESC tuşu ile tam ekrandan çıkış
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isFullscreen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Stats verilerini çek
  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      
      try {
        const [studentsRes, teachersRes, lessonsRes] = await Promise.all([
          axiosConfig.get('/admin/ogrencis', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axiosConfig.get('/admin/teachers', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axiosConfig.get('/admin/dersgrubu', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        // Öğrenci sayısını al
        let studentCount = 0;
        if (studentsRes.data?.pagination?.totalCount !== undefined) {
          studentCount = studentsRes.data.pagination.totalCount;
        } else if (Array.isArray(studentsRes.data)) {
          studentCount = studentsRes.data.length;
        } else if (studentsRes.data?.data && Array.isArray(studentsRes.data.data)) {
          studentCount = studentsRes.data.data.length;
        }

        // Öğretmen sayısını al
        let teacherCount = 0;
        if (teachersRes.data?.pagination?.totalCount !== undefined) {
          teacherCount = teachersRes.data.pagination.totalCount;
        } else if (Array.isArray(teachersRes.data)) {
          teacherCount = teachersRes.data.length;
        } else if (teachersRes.data?.data && Array.isArray(teachersRes.data.data)) {
          teacherCount = teachersRes.data.data.length;
        }

        // Ders grubu sayısını al
        let lessonCount = 0;
        if (lessonsRes.data?.pagination?.totalCount !== undefined) {
          lessonCount = lessonsRes.data.pagination.totalCount;
        } else if (Array.isArray(lessonsRes.data)) {
          lessonCount = lessonsRes.data.length;
        } else if (lessonsRes.data?.data && Array.isArray(lessonsRes.data.data)) {
          lessonCount = lessonsRes.data.data.length;
        }

        const newStats = { studentCount, teacherCount, lessonCount };
        setStats(newStats);

        // localStorage'dan da stats'ları kontrol et
        const dashboardStats = localStorage.getItem('dashboardStats');
        if (dashboardStats) {
          try {
            const parsedStats = JSON.parse(dashboardStats);
            setStats(parsedStats);
          } catch (e) {
            console.error("localStorage stats parse error:", e);
          }
        }

      } catch (error) {
        console.error("Stats veri çekilemedi:", error);
        
        // localStorage'dan stats'ları almayı dene
        const dashboardStats = localStorage.getItem('dashboardStats');
        if (dashboardStats) {
          try {
            const parsedStats = JSON.parse(dashboardStats);
            setStats(parsedStats);
          } catch (e) {
            console.error("localStorage stats parse error:", e);
          }
        }
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    // Tüm admin token'larını temizle
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminName');
    localStorage.removeItem('menuPermissions');
    // Diğer token'ları da temizle güvenlik için
    localStorage.removeItem('teacherToken');
    localStorage.removeItem('studentToken');
    navigate('/admin/login');
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar - Desktop */}
        <div className="hidden lg:block">
          <Sidebar stats={stats} isOpen={true} />
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <div className="lg:hidden">
          <Sidebar 
            stats={stats} 
            isOpen={sidebarOpen} 
            onClose={() => setSidebarOpen(false)} 
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tam ekran bildirimi */}
        {isFullscreen && (
          <div className="fixed top-4 right-4 z-50 bg-black/80 text-white px-4 py-2 rounded-lg flex items-center space-x-2 animate-fadeIn">
            <FiMinimize className="w-4 h-4" />
            <span className="text-sm font-medium">Tam ekran modu aktif - ESC ile çık</span>
            <button
              onClick={toggleFullscreen}
              className="ml-2 px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
            >
              Çık
            </button>
          </div>
        )}
          {/* Header */}
          <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/* Left side */}
                <div className="flex items-center">
                  {/* Mobile menu button */}
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  >
                    <FiMenu className="h-6 w-6" />
                  </button>

                  {/* Search */}
                  <div className="ml-4 lg:ml-0">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiSearch className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Ara..."
                      />
                    </div>
                  </div>
                </div>

                {/* Right side */}
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {/* Fullscreen toggle - Tam ekrandayken daha belirgin */}
                  <button
                    onClick={toggleFullscreen}
                    className={`p-2 rounded-md transition-all ${
                      isFullscreen 
                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    } focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`}
                    title={isFullscreen ? 'Tam Ekrandan Çık (ESC)' : 'Tam Ekran'}
                  >
                    {isFullscreen ? (
                      <FiMinimize className="h-5 w-5" />
                    ) : (
                      <FiMaximize className="h-5 w-5" />
                    )}
                  </button>

                  {/* Dark mode toggle */}
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  >
                    {darkMode ? (
                      <FiSun className="h-5 w-5" />
                    ) : (
                      <FiMoon className="h-5 w-5" />
                    )}
                  </button>

                  {/* Notifications */}
                  <NotificationBell />

                  {/* Profile dropdown */}
                  <div className="relative" ref={profileRef}>
                    <button
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                      className="flex items-center max-w-xs bg-white dark:bg-gray-800 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 lg:p-2 lg:rounded-md lg:hover:bg-gray-50 dark:lg:hover:bg-gray-700"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {adminName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="hidden lg:block text-gray-700 dark:text-gray-200 text-sm font-medium">
                          {adminName}
                        </span>
                        <FiChevronDown className="hidden lg:block h-4 w-4 text-gray-400" />
                      </div>
                    </button>

                    {/* Dropdown menu */}
                    {showProfileMenu && (
                      <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 dark:divide-gray-700 focus:outline-none z-50">
                        <div className="px-4 py-3">
                          <p className="text-sm text-gray-900 dark:text-gray-100">{adminName}</p>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                            admin@eduplatform.com
                          </p>
                        </div>
                        <div className="py-1">
                          <Link
                            to="/admin/profile"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <FiUser className="mr-3 h-4 w-4" />
                            Profil
                          </Link>
                          <Link
                            to="/admin/settings"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <FiSettings className="mr-3 h-4 w-4" />
                            Ayarlar
                          </Link>
                          <Link
                            to="/admin/help"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <FiHelpCircle className="mr-3 h-4 w-4" />
                            Yardım
                          </Link>
                        </div>
                        <div className="py-1">
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <FiLogOut className="mr-3 h-4 w-4" />
                            Çıkış Yap
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Main content */}
          <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            <div className="p-4 sm:p-6 lg:p-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;